import { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import CrudPage from "../../components/dash/CrudPage";
import { StatusBadge, LegalBadge } from "../../components/dash/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/dash/PasswordInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  ACTIVE_OPTIONS, PROJECT_STATUS_OPTIONS, ROLE_OPTIONS, ROLE_LABELS, MEDIA_TYPE_OPTIONS, DOC_TYPE_OPTIONS,
  ACCESS_OPTIONS, OPPORTUNITY_OPTIONS, COFFEE_TYPE_OPTIONS, COFFEE_TYPES, ORG_TYPES, ORG_TYPE_OPTIONS, VERIFY_OPTIONS,
  COLLAB_OPTIONS, PROJECT_TYPE_OPTIONS, PROJECT_TYPES, orgName, formatDate, mediaSrc,
} from "../../lib/labels";
import { OrgPhotosButton, LandPhotosButton } from "./AdminCoffee";
import { OrgLogo, orgLogoSrc } from "../../components/dash/OrgLogo";
import { OrgControl, UserControl, ReviewFlag } from "./AdminControls";
import { PlantingRabAdmin, PlantingPreferenceSummary } from "../../components/PlantingSystem";
import { RejectionBadge } from "../../components/RejectionWarning";

const status = (key = "status") => ({ key, label: "Status", render: (r) => <StatusBadge value={r[key]} /> });
const groupOpt = { optionsUrl: "/admin/farmer-groups", mapOption: (g) => ({ value: g.id, label: g.name }) };
const projectOpt = { optionsUrl: "/admin/projects", mapOption: (p) => ({ value: p.id, label: `${p.projectCode} - ${p.name}` }) };
const mono = (key, label) => ({ key, label, render: (r) => <span className="font-mono text-xs">{r[key] || "-"}</span> });

/* ---------- Farmer groups ---------- */
const GROUP_FIELDS = [
  { name: "orgType", label: "Jenis organisasi", type: "select", options: ORG_TYPE_OPTIONS, required: true },
  { name: "name", label: "Nama kelompok / koperasi tani", required: true },
  { name: "description", label: "Deskripsi", type: "textarea" },
  { name: "address", label: "Alamat" },
  { name: "region", label: "Wilayah", help: "Contoh: Lima Puluh Kota, Sumatera Barat" },
  { name: "shortName", label: "Nama singkat" },
  { name: "province", label: "Provinsi" }, { name: "regency", label: "Kabupaten" },
  { name: "district", label: "Kecamatan" }, { name: "village", label: "Desa / Nagari" },
  { name: "phone", label: "Telepon (internal)" }, { name: "email", label: "Email (internal)" },
  { name: "memberCount", label: "Jumlah anggota", type: "number" },
  { name: "contact", label: "Kontak" },
  { name: "verificationStatus", label: "Verifikasi PT SJI", type: "select", options: VERIFY_OPTIONS },
  { name: "collaborationStatus", label: "Status kolaborasi PT SJI", type: "select", options: COLLAB_OPTIONS },
];
const GROUP_CREATE = [...GROUP_FIELDS,
  { name: "loginEmail", label: "Akun login pengelola - Email", type: "email", help: "Isi untuk langsung membuat akun login Kelompok Tani/Koperasi. Boleh dikosongkan dan dibuat nanti lewat tombol Buat Akun Login." },
  { name: "loginPassword", label: "Akun login pengelola - Kata sandi awal", type: "password", minLength: 8, help: "Minimal 8 karakter. Berikan ke pengelola; mereka bisa menggantinya di menu Profil." },
  { name: "loginName", label: "Akun login pengelola - Nama", help: "Kosongkan untuk memakai nama organisasi." },
];

const CreateLoginButton = ({ row, prefix, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", { name: f.name || row.name, email: f.email, password: f.password, role: "FARMER_GROUP", farmerGroupId: row.id });
      toast.success(`Akun login dibuat. Pengelola bisa masuk dengan ${f.email}`);
      setOpen(false); setF({ name: "", email: "", password: "" }); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <>
      <Button size="sm" variant={row.userCount ? "outline" : "default"} className={row.userCount ? "" : "bg-emerald-800 hover:bg-emerald-900"} onClick={() => setOpen(true)} data-testid={`${prefix}org-create-login-${row.id}`}>
        <KeyRound className="h-3.5 w-3.5 mr-1.5" /> {row.userCount ? "Tambah Akun Login" : "Buat Akun Login"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="org-create-login-dialog">
          <DialogHeader><DialogTitle>Akun login: {row.name}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <Input placeholder={`Nama (kosongkan = ${row.name})`} value={f.name} onChange={set("name")} data-testid="org-login-name" />
            <Input type="email" required placeholder="Email untuk login" value={f.email} onChange={set("email")} data-testid="org-login-email" />
            <Input type="text" required minLength={8} placeholder="Kata sandi awal (min. 8 karakter)" value={f.password} onChange={set("password")} data-testid="org-login-password" />
            <p className="text-xs text-slate-500">Catat email dan sandi ini lalu berikan ke pengelola. Sandi tidak bisa dilihat lagi setelah disimpan.</p>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid="org-login-save-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Buat Akun</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
export const AdminFarmerGroups = () => (
  <CrudPage title="Kelompok Tani / Koperasi Tani" subtitle="Kelola identitas, foto, dan status verifikasi organisasi mitra PT SJI." testid="farmer-groups"
    listUrl="/admin/farmer-groups" updateUrl={(r) => `/admin/farmer-groups/${r.id}`} createLabel="Tambah Organisasi"
    defaults={{ status: "ACTIVE", orgType: "KELOMPOK_TANI", verificationStatus: "PENDING", collaborationStatus: "ACTIVE" }} searchKeys={["name", "address", "region"]} createFields={GROUP_CREATE} editFields={GROUP_FIELDS}
    extraActions={(row, reload, p) => <>{row.status === "ACTIVE" && <CreateLoginButton row={row} prefix={p} onDone={reload} />}<OrgPhotosButton org={row} onSaved={reload} testid={`${p}org-photos-${row.id}`} /><OrgControl row={row} reload={reload} prefix={p} /></>}
    columns={[{ key: "name", label: "Nama", render: (r) => <span className="inline-flex items-center gap-2"><OrgLogo src={orgLogoSrc(r)} size="h-8 w-8" testid={`group-logo-${r.id}`} />{orgName(r)}<RejectionBadge matches={r.rejectionMatches} testid={`group-rejection-${r.id}`} /></span> }, { key: "orgType", label: "Jenis", render: (r) => ORG_TYPES[r.orgType] || "Kelompok Tani" }, { key: "region", label: "Wilayah" },
      { key: "userCount", label: "Pengguna" }, { key: "landCount", label: "Lahan" }, status(), status("verificationStatus"),
      { key: "legalStatus", label: "Legalitas", render: (r) => <LegalBadge value={r.legalStatus} testid={`group-legal-${r.id}`} /> }].map((c, i) => (i === 6 ? { ...c, label: "Verifikasi Identitas" } : c))} />
);

/* ---------- Users ---------- */
const isFarmer = (f) => f.role === "FARMER_GROUP";
const USER_CREATE = [
  { name: "name", label: "Nama lengkap", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "password", label: "Kata sandi awal", type: "password", required: true, minLength: 8, help: "Minimal 8 karakter." },
  { name: "role", label: "Peran", type: "select", options: ROLE_OPTIONS, required: true },
  { name: "farmerGroupId", label: "Kelompok tani", type: "select", required: true, showIf: isFarmer, ...groupOpt },
  { name: "phone", label: "No. WhatsApp", help: "Contoh: 0812xxxx. Dipakai kelompok tani untuk membagikan laporan lewat WhatsApp." },
];
const USER_EDIT = [
  { name: "name", label: "Nama lengkap", required: true },
  { name: "role", label: "Peran", type: "select", options: ROLE_OPTIONS, required: true },
  { name: "farmerGroupId", label: "Kelompok tani", type: "select", required: true, showIf: isFarmer, ...groupOpt },
  { name: "status", label: "Status", type: "select", options: ACTIVE_OPTIONS, required: true },
  { name: "phone", label: "No. WhatsApp" },
];

const SetPasswordButton = ({ row, prefix, onDone }) => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/users/${row.id}/password`, { password: pw });
      toast.success("Kata sandi direset. Sesi lama pengguna dicabut.");
      setOpen(false);
      setPw("");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`${prefix}users-set-password-${row.id}`}>
        <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Reset Sandi
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="set-password-dialog">
          <DialogHeader><DialogTitle>Reset kata sandi: {row.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Kata sandi lama tidak perlu diketahui. Pengguna akan keluar dari semua perangkat dan masuk dengan sandi baru ini.</p>
          <form onSubmit={save} className="space-y-4">
            <PasswordInput minLength={8} required autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Kata sandi baru (min. 8 karakter)" data-testid="set-password-input" />
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid="set-password-save-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SUMMARY_ROLES = [["FARMER_GROUP", "Kelompok Tani / Koperasi"], ["INVESTOR", "Investor"], ["PRIVATE_LAND_OWNER", "Pemilik Lahan"]];
const UserSummary = ({ rows }) => {
  const members = rows.filter((r) => r.role !== "SUPER_ADMIN");
  const active = members.filter((r) => r.status === "ACTIVE");
  const cards = [["total", "Total Pengguna Aktif", active.length],
    ...SUMMARY_ROLES.map(([role, label]) => [role, label, active.filter((r) => r.role === role).length]),
    ["inactive", "Tidak Aktif / Menunggu", members.length - active.length]];
  return (
    <div className="mb-5" data-testid="users-summary">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map(([k, label, n]) => (
          <div key={k} className={`rounded-xl border p-4 ${k === "total" ? "bg-emerald-900 text-white border-emerald-900" : "bg-white"}`} data-testid={`users-summary-${k}`}>
            <p className={`text-xs font-semibold ${k === "total" ? "text-emerald-100" : "text-slate-500"}`}>{label}</p>
            <p className="mt-1 text-2xl font-bold font-mono" data-testid={`users-summary-${k}-value`}>{n}</p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-500">Akun PT SJI Owner / Super Admin tidak ikut dihitung.</p>
    </div>
  );
};

const NewPassword = ({ row }) => {
  const [busy, setBusy] = useState(false);
  const sendLink = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/users/${row.id}/send-reset-link`); toast.success(`Tautan atur ulang dikirim ke ${r.data.email}`); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-1">
      <p className="text-xs">{row.passwordChangedAt ? formatDate(row.passwordChangedAt) : "-"}</p>
      {row.status === "ACTIVE" && row.role !== "SUPER_ADMIN" && <button type="button" disabled={busy} onClick={sendLink} className="text-xs font-semibold text-emerald-800 underline disabled:opacity-50" data-testid={`users-send-reset-${row.id}`}>Kirim tautan atur ulang</button>}
    </div>
  );
};

const USER_COLUMNS = [
  { key: "name", label: "Nama", render: (r) => <span className="block max-w-[10rem] break-words">{r.name}</span> },
  { key: "email", label: "Email", render: (r) => <span className="block max-w-[12rem] break-all">{r.email}</span> },
  { key: "role", label: "Peran", render: (r) => ROLE_LABELS[r.role] },
  { key: "farmerGroupName", label: "Kelompok / Koperasi Tani", render: (r) => (r.farmerGroupName
    ? <span className="inline-flex items-center gap-2"><OrgLogo src={r.farmerGroupLogoUrl ? `${process.env.REACT_APP_BACKEND_URL}${r.farmerGroupLogoUrl}` : null} size="h-8 w-8" testid={`user-group-logo-${r.id}`} />{r.farmerGroupName}</span> : "-") }, status(),
  { key: "lastLoginAt", label: "Login terakhir", render: (r) => (r.lastLoginAt ? formatDate(r.lastLoginAt) : "-") },
  { key: "passwordChangedAt", label: "Sandi diganti", render: (r) => <NewPassword key={`${r.id}-${r.passwordChangedAt}`} row={r} /> },
];
export const AdminUsers = () => {
  const { user: me } = useAuth();
  return (
  <CrudPage title="Pengguna" subtitle="Buat akun, atur peran, kelompok tani, dan status aktif pengguna." testid="users"
    listUrl="/admin/users" updateUrl={(r) => `/admin/users/${r.id}`} createLabel="Tambah Pengguna"
    searchKeys={["name", "email", "farmerGroupName"]} createFields={USER_CREATE} editFields={USER_EDIT.filter((f) => f.name !== "status")}
    summary={(rows) => <UserSummary rows={rows} />} stackActions
    columns={USER_COLUMNS} extraActions={(row, reload, p) => <>{row.id !== me?.id && <SetPasswordButton row={row} prefix={p} onDone={reload} />}<UserControl row={row} reload={reload} prefix={p} /></>} />
  );
};

/* ---------- Investors & assignments ---------- */
const INVESTOR_CREATE = USER_CREATE.filter((f) => f.name !== "farmerGroupId" && f.name !== "role")
  .concat({ name: "role", type: "hidden" });
const ASSIGN_FIELDS = [
  { name: "investorUserId", label: "Investor", type: "select", required: true, optionsUrl: "/admin/users?role=INVESTOR", mapOption: (u) => ({ value: u.id, label: `${u.name} (${u.email})` }) },
  { name: "projectId", label: "Kebun", type: "select", required: true, ...projectOpt },
];
const ToggleAssignment = ({ row, reload, prefix }) => {
  const next = row.status === "ACTIVE" ? "REVOKED" : "ACTIVE";
  const run = async () => {
    try {
      await api.patch(`/admin/investor-projects/${row.id}`, { status: next });
      toast.success(next === "ACTIVE" ? "Akses diaktifkan" : "Akses dicabut");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Button size="sm" variant="outline" onClick={run} data-testid={`${prefix}assignment-toggle-${row.id}`}>
      {next === "ACTIVE" ? "Aktifkan" : "Cabut akses"}
    </Button>
  );
};
export const AdminOwners = () => (
  <CrudPage title="Pemilik Lahan" subtitle="Pemilik kebun kopi pribadi. Pendaftaran baru disetujui atau ditolak lewat menu Manajemen Pengajuan agar alasan dan riwayat tercatat." testid="owners"
    listUrl="/admin/users?role=PRIVATE_LAND_OWNER" updateUrl={(r) => `/admin/users/${r.id}`}
    editFields={USER_EDIT.filter((f) => !["role", "farmerGroupId", "status"].includes(f.name))}
    extraActions={(row, reload, p) => <UserControl row={row} reload={reload} prefix={p} />}
    searchKeys={["name", "email"]} columns={[{ key: "name", label: "Nama" }, { key: "email", label: "Email" }, { key: "phone", label: "Telepon" }, status()]} />
);

export const AdminInvestors = () => (
  <div className="space-y-12" data-testid="admin-investors">
    <CrudPage title="Investor" subtitle="Akun investor dan penugasan kebun. Investor hanya dapat melihat kebun yang ditugaskan." testid="investors"
      listUrl="/admin/users?role=INVESTOR" createUrl="/admin/users" updateUrl={(r) => `/admin/users/${r.id}`}
      createLabel="Tambah Investor" defaults={{ role: "INVESTOR" }} createFields={INVESTOR_CREATE}
      editFields={USER_EDIT.filter((f) => f.name !== "role" && f.name !== "farmerGroupId")}
      searchKeys={["name", "email"]} columns={[{ key: "name", label: "Nama" }, { key: "email", label: "Email" }, { key: "phone", label: "WhatsApp" }, status()]} />
    <CrudPage embedded title="Penugasan Kebun Investor" testid="assignments" listUrl="/admin/investor-projects"
      createLabel="Tugaskan ke Kebun" createFields={ASSIGN_FIELDS}
      extraActions={(row, reload, p) => <ToggleAssignment row={row} reload={reload} prefix={p} />}
      columns={[{ key: "investorName", label: "Investor" }, { key: "investorEmail", label: "Email" },
        mono("projectCode", "Kode Kebun"), { key: "projectName", label: "Kebun" }, status(),
        { key: "createdAt", label: "Ditugaskan", render: (r) => formatDate(r.createdAt) }]} />
  </div>
);

/* ---------- Land ---------- */
const LAND_FIELDS = [
  { name: "landCode", label: "Kode lahan", required: true },
  { name: "name", label: "Nama lahan", required: true },
  { name: "farmerGroupId", label: "Kelompok tani", type: "select", required: true, ...groupOpt },
  { name: "areaHectares", label: "Luas (hektar)", type: "number", required: true },
  { name: "plantingSystem", label: "Pola Tanam Kopi (wajib untuk lahan baru)", type: "select", options: [{ value: "STANDARD_1600", label: "Pola Standar — 1.600 batang/ha (Estimasi Modal Awal Rp120–130 juta/ha)" }, { value: "INTENSIVE_2200", label: "Pola Intensif — 2.200 batang/ha, jarak ±1,5 × 3 m (Estimasi Modal Awal Rp155–165 juta/ha)" }, { value: "SUPER_INTENSIVE_3300", label: "Pola Sangat Intensif — 3.300 batang/ha, jarak ±1 × 3 m (Estimasi Modal Awal Rp210–230 juta/ha)" }], help: "Kapasitas & target populasi dihitung otomatis: luas × batang/ha pola. Estimasi modal merupakan kisaran awal, bukan RAB final." },
  { name: "latitude", label: "Latitude", type: "number" },
  { name: "longitude", label: "Longitude", type: "number" },
  { name: "status", label: "Status", type: "select", options: ACTIVE_OPTIONS, required: true },
  { name: "opportunityStatus", label: "Status peluang investasi", type: "select", options: OPPORTUNITY_OPTIONS, help: "Pilih 'Lahan Tersedia' agar tampil di halaman publik (setelah lahan diverifikasi)." },
  { name: "province", label: "Provinsi" }, { name: "regency", label: "Kabupaten" },
  { name: "district", label: "Kecamatan" }, { name: "village", label: "Desa / Nagari" },
  { name: "elevation", label: "Ketinggian (mdpl)", type: "number" }, { name: "accessRoad", label: "Akses jalan" },
  { name: "landCondition", label: "Kondisi lahan", help: "Contoh: Lahan kosong / belum ditanami" },
  { name: "slope", label: "Kemiringan" }, { name: "waterAccess", label: "Akses air" },
  { name: "vegetation", label: "Vegetasi saat ini" },
  { name: "coffeeType", label: "Rencana jenis kopi", type: "select", options: COFFEE_TYPE_OPTIONS },
  { name: "coffeeVariety", label: "Varietas" }, { name: "targetTrees", label: "Target populasi (pohon)", type: "number", help: "Untuk lahan dengan Pola Tanam, dihitung otomatis dari pola (isian manual diabaikan)." },
  { name: "plantingSpacing", label: "Jarak tanam", help: "Pola Intensif otomatis ±1,5 × 3 meter." }, { name: "estPlantingDate", label: "Perkiraan waktu tanam" },
  { name: "developmentPlan", label: "Rencana pembangunan awal", type: "textarea" },
  { name: "estFirstHarvest", label: "Perkiraan panen pertama" }, { name: "projectDuration", label: "Durasi kerja sama" },
];
const coords = (r) => (r.latitude != null && r.longitude != null ? <span className="font-mono text-xs">{r.latitude}, {r.longitude}</span> : "-");
export const AdminLand = () => (
  <div className="space-y-4">
  <PlantingRabAdmin />
  <PlantingPreferenceSummary />
  <CrudPage title="Lahan Kopi" subtitle="Lahan lama yang dicatat PT SJI. Lahan baru dikelola Kelompok Tani/Koperasi lewat Manajemen Lahan (lihat menu Pemantauan Lahan)." testid="land"
    listUrl="/admin/land" updateUrl={(r) => `/admin/land/${r.id}`} createLabel="Tambah Lahan" defaults={{ status: "ACTIVE", opportunityStatus: "NOT_LISTED", coffeeType: "ROBUSTA" }}
    searchKeys={["landCode", "name", "farmerGroupName"]} createFields={LAND_FIELDS} editFields={LAND_FIELDS}
    extraActions={(row, _r, p) => <LandPhotosButton land={row} testid={`${p}land-photos-${row.id}`} />}
    columns={[mono("landCode", "Kode"), { key: "name", label: "Nama" }, { key: "farmerGroupName", label: "Kelompok / Koperasi Tani" },
      { key: "areaHectares", label: "Luas (ha)" }, { key: "coords", label: "Koordinat", render: coords }, status(),
      status("verificationStatus"), status("opportunityStatus")].map((c, i) => (i === 6 ? { ...c, label: "Verifikasi" } : i === 7 ? { ...c, label: "Peluang" } : c))} />
  </div>
);

/* ---------- Projects ---------- */
const PROJECT_FIELDS = [
  { name: "projectCode", label: "Kode kebun", required: true },
  { name: "name", label: "Nama kebun", required: true },
  { name: "description", label: "Deskripsi", type: "textarea" },
  { name: "landParcelId", label: "Lahan", type: "select", required: true, optionsUrl: "/admin/land", mapOption: (l) => ({ value: l.id, label: `${l.landCode} - ${l.name}` }), help: "Kelompok tani kebun mengikuti pemilik lahan." },
  { name: "projectType", label: "Skema kebun", type: "select", options: PROJECT_TYPE_OPTIONS, required: true },
  { name: "ownerUserId", label: "Pemilik lahan (khusus kebun pribadi)", type: "select", optionsUrl: "/admin/users?role=PRIVATE_LAND_OWNER", mapOption: (u) => ({ value: u.id, label: `${u.name} (${u.email})` }), help: "Kosongkan untuk Kebun Kopi dengan Pemodal." },
  { name: "status", label: "Tahap kebun kopi", type: "select", options: PROJECT_STATUS_OPTIONS, required: true },
  { name: "coffeeType", label: "Jenis kopi", type: "select", options: COFFEE_TYPE_OPTIONS },
  { name: "coffeeVariety", label: "Varietas kopi" },
  { name: "startDate", label: "Tanggal mulai investasi", type: "date" },
  { name: "budgetTotal", label: "Total anggaran (Rp)", type: "number" },
  { name: "targetTrees", label: "Target populasi (pohon)", type: "number" },
  { name: "currentTrees", label: "Populasi saat ini", type: "number" },
  { name: "livingTrees", label: "Pohon hidup", type: "number" }, { name: "deadTrees", label: "Pohon mati", type: "number" },
  { name: "replacementTrees", label: "Pohon sulaman", type: "number" },
  { name: "plantingDate", label: "Tanggal tanam", type: "date" }, { name: "plantingSpacing", label: "Jarak tanam" },
  { name: "shadeTrees", label: "Informasi pohon naungan" }, { name: "floweringStatus", label: "Status pembungaan" },
  { name: "fruitStatus", label: "Perkembangan buah" }, { name: "harvestStatus", label: "Status panen" },
  { name: "currentStage", label: "Catatan tahap" },
];
export const AdminProjects = () => (
  <CrudPage title="Kebun Kopi" subtitle="Kebun pembangunan kebun kopi terhubung ke satu lahan dan satu kelompok tani." testid="projects"
    listUrl="/admin/projects" updateUrl={(r) => `/admin/projects/${r.id}`} createLabel="Tambah Rencana Kebun" defaults={{ status: "DRAFT", coffeeType: "ROBUSTA", projectType: "INVESTMENT_COFFEE_PROJECT" }}
    searchKeys={["projectCode", "name", "farmerGroupName"]} createFields={PROJECT_FIELDS} editFields={PROJECT_FIELDS}
    extraActions={(row, reload, p) => <><Button asChild size="sm" variant="outline" data-testid={`${p}projects-view-${row.id}`}><Link to={`/dashboard/admin/proyek/${row.id}`}>Lihat</Link></Button><ReviewFlag row={row} reload={reload} prefix={p} /></>}
    columns={[mono("projectCode", "Kode"), { key: "name", label: "Nama" }, { key: "farmerGroupName", label: "Kelompok / Koperasi Tani" },
      mono("landCode", "Lahan"), { key: "coffeeType", label: "Jenis Kopi", render: (r) => COFFEE_TYPES[r.coffeeType] || "-" }, { key: "projectType", label: "Skema", render: (r) => PROJECT_TYPES[r.projectType] || PROJECT_TYPES.INVESTMENT_COFFEE_PROJECT }, { key: "investorCount", label: "Investor" }, status(),
      { key: "reviewFlag", label: "Pemeriksaan PT SJI", render: (r) => (r.reviewFlag ? <span className="text-xs font-semibold text-amber-800" data-testid={`project-review-flag-${r.id}`}>Perlu diperiksa: {r.reviewFlag.reason}</span> : "-") }]} />
);

/* ---------- Media, documents, updates ---------- */
export const MediaPreview = ({ item }) =>
  item.type === "PHOTO" ? (
    <img src={mediaSrc(item)} alt={item.caption || "media"} className="h-12 w-16 object-cover rounded-md border" />
  ) : (
    <a href={mediaSrc(item)} target="_blank" rel="noopener noreferrer" className="text-emerald-800 inline-flex items-center gap-1 text-xs font-semibold">
      {item.type === "VIDEO" ? "Video" : "File"} <ExternalLink className="h-3 w-3" />
    </a>
  );
const FileLink = ({ url }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className="text-emerald-800 inline-flex items-center gap-1 text-xs font-semibold">
    Buka <ExternalLink className="h-3 w-3" />
  </a>
);
const MEDIA_FIELDS = [
  { name: "projectId", label: "Kebun", type: "select", required: true, ...projectOpt },
  { name: "type", label: "Jenis", type: "select", options: MEDIA_TYPE_OPTIONS, required: true },
  { name: "fileUrl", label: "URL file (https://)", required: true, placeholder: "https://..." },
  { name: "caption", label: "Keterangan" },
];
const DOC_FIELDS = [
  { name: "projectId", label: "Kebun", type: "select", required: true, ...projectOpt },
  { name: "title", label: "Judul dokumen", required: true },
  { name: "documentType", label: "Jenis dokumen", type: "select", options: DOC_TYPE_OPTIONS, required: true },
  { name: "fileUrl", label: "URL file (https://)", required: true, placeholder: "https://..." },
  { name: "accessLevel", label: "Hak akses", type: "select", options: ACCESS_OPTIONS, required: true },
];
const accessLabel = (v) => ACCESS_OPTIONS.find((o) => o.value === v)?.label || v;

export const AdminMedia = () => (
  <div data-testid="admin-media">
    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-1.5" data-testid="page-title">Media & Dokumen</h1>
    <p className="text-sm text-slate-600 mb-8">Seluruh foto, video, dokumen, dan update lapangan dari semua kebun.</p>
    <Tabs defaultValue="media">
      <TabsList className="mb-6">
        <TabsTrigger value="media" data-testid="tab-media">Foto & Video</TabsTrigger>
        <TabsTrigger value="documents" data-testid="tab-documents">Dokumen</TabsTrigger>
        <TabsTrigger value="updates" data-testid="tab-updates">Update Lapangan</TabsTrigger>
      </TabsList>
      <TabsContent value="media">
        <CrudPage embedded title="Foto & Video" testid="media" listUrl="/admin/media" createLabel="Tambah Media"
          createFields={MEDIA_FIELDS} defaults={{ type: "PHOTO" }}
          columns={[{ key: "preview", label: "Pratinjau", render: (r) => <MediaPreview item={r} /> }, mono("projectCode", "Kebun"),
            { key: "caption", label: "Keterangan" }, { key: "uploaderName", label: "Diunggah oleh" }, status("verificationStatus"),
            { key: "createdAt", label: "Tanggal", render: (r) => formatDate(r.createdAt) }]} />
      </TabsContent>
      <TabsContent value="documents">
        <CrudPage embedded title="Dokumen" testid="documents" listUrl="/admin/documents" createLabel="Tambah Dokumen"
          createFields={DOC_FIELDS} defaults={{ accessLevel: "ADMIN_ONLY", documentType: "REPORT" }}
          columns={[{ key: "title", label: "Judul" }, mono("projectCode", "Kebun"), { key: "documentType", label: "Jenis", render: (r) => DOC_TYPE_OPTIONS.find((o) => o.value === r.documentType)?.label },
            { key: "accessLevel", label: "Akses", render: (r) => accessLabel(r.accessLevel) }, { key: "fileUrl", label: "File", render: (r) => <FileLink url={r.fileUrl} /> }]} />
      </TabsContent>
      <TabsContent value="updates">
        <CrudPage embedded title="Update Lapangan" testid="admin-updates" listUrl="/admin/farm-updates"
          columns={[{ key: "title", label: "Judul" }, mono("projectCode", "Kebun"), { key: "uploaderName", label: "Dibuat oleh" }, status(),
            { key: "createdAt", label: "Tanggal", render: (r) => formatDate(r.createdAt) }]} />
      </TabsContent>
    </Tabs>
  </div>
);
